/** @jsxImportSource solid-js */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-namespace */

import { customElement } from 'solid-element';
import CustomCheckbox, { type CustomCheckboxProps } from './CustomCheckbox';
import { ICustomElement } from 'component-register';

// Extended interface for web component with imperative methods
export interface CustomCheckboxElement extends HTMLElement {
  focus(): void;
  click(): void;
}

/**
 * Usage example for imperative focus:
 *
 * const checkboxRef = useRef<HTMLElement>();
 *
 * // Call focus method directly on the element
 * const handleFocus = () => {
 *   if (checkboxRef.current && 'focus' in checkboxRef.current) {
 *     (checkboxRef.current as CustomCheckboxElement).focus();
 *   }
 * };
 *
 * <tc-custom-checkbox ref={checkboxRef} name="example" value="option1" />
 */

// TypeScript declaration for the web component
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'tc-custom-checkbox': Partial<CustomCheckboxElement> &
        Omit<CustomCheckboxProps, 'onInputRef'> &
        Partial<{ ref: ICustomElement }>;
    }
  }
}

export function defineSolidCustomCheckbox(): void {
  customElement(
    'tc-custom-checkbox',
    {
      // Include ALL component properties with default values (excluding onInputRef)
      name: '',
      value: '',
      id: undefined as unknown as string | undefined,
      checked: false,
      disabled: false,
      required: false,
      'data-cy': undefined as unknown as string | undefined,
      dataCy: undefined as unknown as string | undefined,

      // Internal callback for imperative access - not exposed as a web component prop
      onInputRef: undefined as unknown as CustomCheckboxProps['onInputRef'],

      // Event props present for completeness; events are dispatched as CustomEvents
      onChange: undefined as unknown as CustomCheckboxProps['onChange'],
      onInput: undefined as unknown as CustomCheckboxProps['onInput'],
      onBlur: undefined as unknown as CustomCheckboxProps['onBlur'],
      onFocus: undefined as unknown as CustomCheckboxProps['onFocus'],
    },
    (props, { element }) => {
      let inputRef: HTMLInputElement | undefined;

      // Attach imperative methods to the web component element
      (element as unknown as CustomCheckboxElement).focus = () => {
        if (inputRef) {
          inputRef.focus();
        }
      };

      (element as unknown as CustomCheckboxElement).click = () => {
        if (inputRef) {
          inputRef.click();
        }
      };

      // Filter out onInputRef from props since we handle it separately
      const { onInputRef: _, ...componentProps } = props;

      return (
        <CustomCheckbox
          {...componentProps}
          onInputRef={(ref) => {
            inputRef = ref;
          }}
        />
      );
    }
  );
}

export type { CustomCheckboxProps };
